// client/src/components/pages/Geofencing/GeofenceEventsFeed.tsx (converted from .js, ST-00269)
//
// Geofences > Events: what happened at the zones, from LexiStar (the legacy geofence
// evaluator and its geofence_events rows are retired). Zone events (entered, exited, stayed
// too long, left too soon, no-go entry, outside operating hours, speeding in the zone, site
// dwell over the limit) of the organisation, newest first, with filters: dates (the last 7
// days by default), type, zone, vehicle and Show (all events, alerts, open alerts). An ALERT
// is an event a zone rule asked for; an open alert can be acknowledged here (LexiStar's own
// acknowledge, so its escalation stops) and counts as a Pending Alert until then.
//
// The list loads from GET /api/geofence-events with the filters; events that arrive live
// (useTrackingFeed's `lexistar:event`) are added on top when they match them.
//
// ST-00282: Acknowledge all acts on the list AS IT WAS LOADED. The server answers each load with
// the open alerts matching its filters (whatever Show is), the database snapshot it read them
// with and when (asOf); "Show more" reads further pages from that same snapshot. "Acknowledge
// all (N)" asks in the page (never a browser dialog) "Acknowledge N open alerts?" and sends the
// filters the list was loaded with, its snapshot and N: the server acknowledges exactly the open
// alerts that snapshot saw — never one recorded after the list was read — and only when exactly N
// of them are still open (else 409 ALERTS_CHANGED and nothing is acknowledged: the list reloads
// and the user confirms the new number), in calls of up to 1000, each alert exactly as a single
// Acknowledge (its escalation stops, it is audited). Changing a filter (or Refresh) closes the
// question; while the list loads there is no Acknowledge all. A single Acknowledge on an alert of
// the loaded list takes one off N. `acknowledgedHint` (the server's `lexistar:events_acknowledged`,
// sent after the commit) is a hint, applied when the list is idle — at once, or after the
// question is answered: the loaded rows it names turn acknowledged in place and N is read again
// for the same snapshot, so the user keeps their place ("Show more" pages included).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { AxiosError } from 'axios';
import {
  FaArrowRight,
  FaArrowLeft,
  FaBan,
  FaCheck,
  FaCheckDouble,
  FaClock,
  FaExclamationTriangle,
  FaSync,
  FaTachometerAlt,
} from 'react-icons/fa';
import api from '../../../services/api';
import { ThemedSelect, UnifiedDateRangePicker } from '../../../types/commonComponents';
import { formatAppDateTime, formatRelativeTime, toApiDateString } from '../../../utils/dateUtils';
import {
  SEVERITY_LABELS,
  SHOW_OPTIONS,
  ZONE_EVENT_TYPES,
  eventDetail,
  isPendingAlert,
  matchesFilters,
  typeLabel,
  typeTone,
  type ZoneEventFilters,
  type ZoneEventTone,
} from './zoneEvents';
import type { AcknowledgedHint } from './useTrackingFeed';
import type {
  GeofenceRow,
  ZoneAlertsAcknowledgeAllBody,
  ZoneAlertsAcknowledgeAllResponse,
  ZoneAlertsChangedError,
  ZoneEvent,
  ZoneEventShow,
  ZoneEventsResponse,
} from '../../../types/clientPages/geofencing';

const PAGE_SIZE = 50;

const TONES: Record<ZoneEventTone, { icon: ReactNode; dot: string; badge: string }> = {
  entry: {
    icon: <FaArrowRight />,
    dot: 'bg-green-50 text-green-600',
    badge: 'bg-green-100 text-green-700',
  },
  exit: {
    icon: <FaArrowLeft />,
    dot: 'bg-slate-100 text-slate-600',
    badge: 'bg-slate-100 text-slate-700',
  },
  dwell: {
    icon: <FaClock />,
    dot: 'bg-amber-50 text-amber-600',
    badge: 'bg-amber-100 text-amber-700',
  },
  danger: { icon: <FaBan />, dot: 'bg-red-50 text-red-600', badge: 'bg-red-100 text-red-700' },
  warning: {
    icon: <FaTachometerAlt />,
    dot: 'bg-orange-50 text-orange-600',
    badge: 'bg-orange-100 text-orange-700',
  },
  info: {
    icon: <FaExclamationTriangle />,
    dot: 'bg-gray-100 text-gray-600',
    badge: 'bg-gray-100 text-gray-700',
  },
};

const SEVERITY_CLASS: Record<string, string> = {
  M: 'bg-amber-100 text-amber-800',
  H: 'bg-orange-100 text-orange-800',
  C: 'bg-red-100 text-red-800',
};

const daysAgo = (n: number): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - n);
  return d;
};

const errorText = (err: unknown, fallback: string): string =>
  (err as AxiosError<{ error?: string }>)?.response?.data?.error || fallback;

/** A 409 ALERTS_CHANGED refusal of Acknowledge all, or null. */
const alertsChanged = (err: unknown): ZoneAlertsChangedError | null => {
  const data = (err as AxiosError<ZoneAlertsChangedError>)?.response?.data;
  return data && data.code === 'ALERTS_CHANGED' ? data : null;
};

const NO_HINT: AcknowledgedHint = { nonce: 0, ids: [] };

const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n.toLocaleString('en-ZA')} ${n === 1 ? one : many}`;

/** "10:42:05" in the app's time zone (when the list was read, in the question). */
const clockTime = (iso: string | null): string =>
  iso
    ? new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Africa/Johannesburg',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      }).format(new Date(iso))
    : '';

/** The list as it was loaded: what Acknowledge all acts on (ST-00282). */
interface LoadedList {
  /** The filters of the request that loaded it (the server's window for the dates). */
  query: Pick<ZoneAlertsAcknowledgeAllBody, 'from' | 'to' | 'type' | 'geofenceId' | 'regNo'>;
  /** The database snapshot it was read with (null: a server without migration 072). */
  snapshot: string | null;
  asOf: string | null;
  /** Open alerts in it (whatever Show is), less the ones acknowledged here since. */
  openAlerts: number;
}

/** Acknowledge all's state: asking, working (with progress), or idle. */
type AckAllState =
  | { phase: 'idle' }
  | { phase: 'confirm'; count: number }
  | { phase: 'working'; count: number; done: number };

export interface GeofenceEventsFeedProps {
  /** The organisation's zones (the Zone filter). */
  geofences?: GeofenceRow[];
  /** Zone events that arrived live since the page opened, newest first. */
  liveEvents?: ZoneEvent[];
  /** Acknowledge one open alert; the acknowledged event, or null when it failed. */
  onAcknowledge: (eventId: string) => Promise<ZoneEvent | null>;
  /**
   * ST-00282: one Acknowledge all call (useTrackingFeed.acknowledgeAll). Without it the list
   * offers no Acknowledge all.
   */
  onAcknowledgeAll?: (body: ZoneAlertsAcknowledgeAllBody) => Promise<ZoneAlertsAcknowledgeAllResponse>;
  /** ST-00282: changes when the server says alerts were acknowledged (the list re-reads). */
  acknowledgedHint?: AcknowledgedHint;
  /** The Show filter the tab opens on (e.g. Open alerts from the Pending Alerts card). */
  initialShow?: ZoneEventShow;
}

export default function GeofenceEventsFeed({
  geofences = [],
  liveEvents = [],
  onAcknowledge,
  onAcknowledgeAll,
  acknowledgedHint = NO_HINT,
  initialShow = 'all',
}: GeofenceEventsFeedProps) {
  const [startDate, setStartDate] = useState<Date>(() => daysAgo(6));
  const [endDate, setEndDate] = useState<Date>(() => daysAgo(0));
  const [type, setType] = useState('');
  const [geofenceId, setGeofenceId] = useState('');
  const [regInput, setRegInput] = useState('');
  const [regNo, setRegNo] = useState('');
  const [show, setShow] = useState<ZoneEventShow>(initialShow);
  const [items, setItems] = useState<ZoneEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ackError, setAckError] = useState<string | null>(null);
  const [acking, setAcking] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const requestSeq = useRef(0);
  // ST-00282: the list as loaded, Acknowledge all, and the server's hints.
  const [loaded, setLoaded] = useState<LoadedList | null>(null);
  const [ackAll, setAckAll] = useState<AckAllState>({ phase: 'idle' });
  const [ackAllNotice, setAckAllNotice] = useState<string | null>(null);
  const [hintPending, setHintPending] = useState(false);
  /** Single acknowledgements made here: their hint needs no re-read (already shown). */
  const ownAcks = useRef<Set<string>>(new Set());

  useEffect(() => setShow(initialShow), [initialShow]);

  // The vehicle box filters once typing pauses.
  useEffect(() => {
    const t = window.setTimeout(() => setRegNo(regInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [regInput]);

  const filters: ZoneEventFilters = useMemo(
    () => ({
      from: toApiDateString(startDate),
      to: toApiDateString(endDate),
      type,
      geofenceId,
      regNo,
      show,
    }),
    [startDate, endDate, type, geofenceId, regNo, show]
  );

  const params = useCallback(
    (offset: number) => ({
      from: filters.from,
      to: filters.to,
      show: filters.show,
      limit: PAGE_SIZE,
      offset,
      ...(filters.type ? { type: filters.type } : {}),
      ...(filters.geofenceId ? { geofenceId: filters.geofenceId } : {}),
      ...(filters.regNo ? { regNo: filters.regNo } : {}),
    }),
    [filters]
  );

  useEffect(() => {
    const seq = ++requestSeq.current;
    const query = params(0);
    setLoading(true);
    setError(null);
    // A new read: the list Acknowledge all would act on is changing, so the question closes.
    setLoaded(null);
    setAckAll((s) => (s.phase === 'confirm' ? { phase: 'idle' } : s));
    api.geofenceEvents
      .list(query)
      .then((res) => {
        if (seq !== requestSeq.current) return;
        const body = res.data as ZoneEventsResponse | undefined;
        setItems(body?.data || []);
        setTotal(body?.total || 0);
        setAvailable(body?.available !== false);
        setLoaded({
          query: {
            from: body?.window?.from || query.from,
            to: body?.window?.to || query.to,
            ...(query.type ? { type: query.type } : {}),
            ...(query.geofenceId ? { geofenceId: query.geofenceId } : {}),
            ...(query.regNo ? { regNo: query.regNo } : {}),
          },
          snapshot: body?.snapshot ?? null,
          asOf: body?.asOf ?? null,
          openAlerts: body?.openAlerts ?? 0,
        });
      })
      .catch((err: unknown) => {
        if (seq !== requestSeq.current) return;
        setError(errorText(err, 'Could not load the zone events.'));
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  }, [params, reloadNonce]);

  // ST-00282: alerts acknowledged elsewhere (another controller, or anyone's Acknowledge all), as
  // the server says after its commit. The list is NOT read again from the top (that would drop
  // the "Show more" pages and the user's place): once it is idle, the loaded rows it names turn
  // acknowledged in place (Open alerts drops them) and the open count is read again for the SAME
  // snapshot, so Acknowledge all still acts on the list as loaded. A burst is coalesced; the
  // single acknowledgements made here are already shown.
  const hintedIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (acknowledgedHint.nonce === 0) return;
    const ids = acknowledgedHint.ids;
    if (ids.length > 0 && ids.every((id) => ownAcks.current.has(id))) {
      ids.forEach((id) => ownAcks.current.delete(id));
      return;
    }
    ids.forEach((id) => hintedIds.current.add(id));
    setHintPending(true);
  }, [acknowledgedHint]);
  useEffect(() => {
    if (!hintPending || ackAll.phase !== 'idle' || loadingMore || loading) return undefined;
    const t = window.setTimeout(() => {
      setHintPending(false);
      const ids = hintedIds.current;
      hintedIds.current = new Set();
      const list = loaded;
      if (!list?.snapshot) {
        // A server without the snapshot: nothing to keep consistent with — read it again.
        setReloadNonce((n) => n + 1);
        return;
      }
      const removed = show === 'open' ? items.filter((e) => ids.has(e.id) && e.status === 'open').length : 0;
      setItems((prev) => {
        const next = prev.map((e) => (ids.has(e.id) && e.status === 'open' ? { ...e, status: 'acknowledged' } : e));
        return show === 'open' ? next.filter((e) => !(ids.has(e.id) && e.status === 'acknowledged')) : next;
      });
      if (removed) setTotal((n) => Math.max(0, n - removed));
      const seq = requestSeq.current;
      api.geofenceEvents
        .list({ ...params(0), limit: 1, snapshot: list.snapshot })
        .then((res) => {
          if (seq !== requestSeq.current) return; // a new read replaced this list meanwhile
          const body = res.data as ZoneEventsResponse | undefined;
          if (typeof body?.openAlerts === 'number') {
            setLoaded((l) => (l && l.snapshot === list.snapshot ? { ...l, openAlerts: body.openAlerts as number } : l));
          }
          if (typeof body?.total === 'number') setTotal(body.total);
        })
        .catch(() => setReloadNonce((n) => n + 1));
    }, 300);
    return () => window.clearTimeout(t);
  }, [hintPending, ackAll.phase, loadingMore, loading, loaded, items, show, params]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      // The loaded list's snapshot: further pages of the same list.
      const res = await api.geofenceEvents.list({
        ...params(items.length),
        ...(loaded?.snapshot ? { snapshot: loaded.snapshot } : {}),
      });
      const body = res.data as ZoneEventsResponse | undefined;
      const seen = new Set(items.map((e) => e.id));
      setItems((prev) => [...prev, ...(body?.data || []).filter((e) => !seen.has(e.id))]);
      setTotal(body?.total || total);
    } catch (err) {
      setError(errorText(err, 'Could not load more zone events.'));
    } finally {
      setLoadingMore(false);
    }
  };

  // Live arrivals that match the filters, above the loaded page (each once). They arrived after
  // the list was read: Acknowledge all leaves them (Refresh takes them into the list).
  const shown = useMemo(() => {
    const inList = new Set(items.map((e) => e.id));
    const fresh = liveEvents.filter((e) => !inList.has(e.id) && matchesFilters(e, filters));
    return [...fresh, ...items];
  }, [items, liveEvents, filters]);
  const extra = shown.length - items.length;

  const canAcknowledgeAll =
    !!onAcknowledgeAll && available && !loading && !!loaded?.snapshot && loaded.openAlerts > 0;

  const askAcknowledgeAll = () => {
    if (!loaded) return;
    setAckAllNotice(null);
    setAckError(null);
    setAckAll({ phase: 'confirm', count: loaded.openAlerts });
  };

  const runAcknowledgeAll = async () => {
    const list = loaded;
    if (!onAcknowledgeAll || !list?.snapshot) return;
    const body: Omit<ZoneAlertsAcknowledgeAllBody, 'expectedCount'> = {
      ...list.query,
      snapshot: list.snapshot,
      asOf: list.asOf,
    };
    const count = list.openAlerts;
    let done = 0;
    let expected = count;
    setAckAll({ phase: 'working', count, done });
    try {
      // At most 1000 per call: again, with what is left (counted by the server), until nothing is.
      for (let call = 0; call < 1000; call++) {
        const res = await onAcknowledgeAll({ ...body, expectedCount: expected });
        done += res.acknowledged;
        setAckAll({ phase: 'working', count, done });
        if (!(res.remaining > 0) || res.acknowledged === 0) break;
        expected = res.remaining;
      }
      setAckAll({ phase: 'idle' });
      setAckAllNotice(
        done > 0
          ? `Acknowledged ${plural(done, 'alert')}.`
          : 'Nothing to acknowledge: these alerts were already acknowledged.'
      );
      setReloadNonce((n) => n + 1);
    } catch (err) {
      const changed = alertsChanged(err);
      setAckAll({ phase: 'idle' });
      if (changed) {
        // A different number is open than confirmed (someone acknowledged some meanwhile):
        // nothing more was acknowledged. Read the list again and ask about it.
        setAckAllNotice(
          `${done > 0 ? `Acknowledged ${plural(done, 'alert')}, then the` : 'The'} alerts changed while you looked: ` +
            `${plural(changed.count, 'open alert')} ${changed.count === 1 ? 'matches' : 'match'} the list now. ` +
            'Check the list and use Acknowledge all again.'
        );
        setReloadNonce((n) => n + 1);
        return;
      }
      setAckError(
        `${done > 0 ? `Acknowledged ${plural(done, 'alert')}, then stopped: ` : ''}${errorText(err, 'Could not acknowledge the alerts. Refresh and try again.')}`
      );
      if (done > 0) setReloadNonce((n) => n + 1);
    }
  };

  const acknowledge = async (event: ZoneEvent) => {
    setAcking(event.id);
    setAckError(null);
    // Its hint (sent after the commit) may come before this answer.
    ownAcks.current.add(event.id);
    const done = await onAcknowledge(event.id);
    setAcking(null);
    if (!done) {
      ownAcks.current.delete(event.id);
      setAckError(
        `Could not acknowledge the alert for ${event.regNo || 'this event'}. Refresh and try again.`
      );
      return;
    }
    // One of the loaded list's open alerts (not a live arrival) leaves the number to confirm.
    const ofList = items.some((e) => e.id === event.id && isPendingAlert(e));
    setItems((prev) => {
      const next = prev.map((e) => (e.id === event.id ? { ...e, ...done } : e));
      // "Open alerts" lists only what is still open.
      return show === 'open' ? next.filter((e) => e.id !== event.id) : next;
    });
    if (show === 'open') setTotal((t) => Math.max(0, t - 1));
    if (ofList) {
      setLoaded((l) => (l ? { ...l, openAlerts: Math.max(0, l.openAlerts - 1) } : l));
    }
  };

  const labelClass = 'block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5';

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900">
          Zone events{' '}
          <span className="text-gray-500 font-normal tabular-nums">({total + extra})</span>
        </h2>
        <div className="flex items-center gap-2">
          {canAcknowledgeAll && ackAll.phase === 'idle' && (
            <button
              type="button"
              onClick={askAcknowledgeAll}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-surface border border-gray-300 hover:border-green-500 hover:text-green-700 rounded-lg transition-colors"
            >
              <FaCheckDouble size={12} aria-hidden="true" />
              Acknowledge all
              <span className="tabular-nums text-gray-500">
                ({(loaded?.openAlerts ?? 0).toLocaleString('en-ZA')})
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setReloadNonce((n) => n + 1)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <FaSync size={12} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </div>

      {ackAll.phase === 'confirm' && loaded && (
        <div
          role="alertdialog"
          aria-labelledby="ack-all-title"
          aria-describedby="ack-all-detail"
          className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
        >
          <p id="ack-all-title" className="text-sm font-semibold text-gray-900">
            Acknowledge {plural(ackAll.count, 'open alert')}?
          </p>
          <p id="ack-all-detail" className="mt-1 text-sm text-gray-700">
            These are the open alerts in the list as you loaded it at {clockTime(loaded.asOf)},
            with its dates, type, zone and vehicle. Each is acknowledged as if you pressed
            Acknowledge on it: its escalation stops and it is recorded that you did it. Alerts
            that came in after you loaded the list stay open. If the number of open alerts has
            changed meanwhile, nothing is acknowledged and you are asked again.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => runAcknowledgeAll()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg"
            >
              <FaCheckDouble size={12} aria-hidden="true" />
              Acknowledge {plural(ackAll.count, 'alert')}
            </button>
            <button
              type="button"
              onClick={() => setAckAll({ phase: 'idle' })}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-surface border border-gray-300 hover:bg-gray-50 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {ackAll.phase === 'working' && (
        <div
          role="status"
          className="mb-4 flex items-center gap-2 rounded-lg border border-gray-200 bg-surface px-4 py-3 text-sm text-gray-700"
        >
          <span
            className="inline-block w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          />
          Acknowledging… {ackAll.done.toLocaleString('en-ZA')} of {ackAll.count.toLocaleString('en-ZA')}
        </div>
      )}

      {ackAllNotice && (
        <div
          role="status"
          className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800"
        >
          <span>{ackAllNotice}</span>
          <button
            type="button"
            onClick={() => setAckAllNotice(null)}
            className="text-xs font-semibold underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <div>
          <span className={labelClass}>Dates</span>
          <UnifiedDateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </div>
        <div>
          <label htmlFor="zone-events-show" className={labelClass}>
            Show
          </label>
          <ThemedSelect
            id="zone-events-show"
            aria-label="Show"
            value={show}
            onChange={(e) => setShow(e.target.value as ZoneEventShow)}
          >
            {SHOW_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div>
          <label htmlFor="zone-events-type" className={labelClass}>
            Type
          </label>
          <ThemedSelect
            id="zone-events-type"
            aria-label="Type"
            value={type}
            onChange={(e) => setType(String(e.target.value ?? ''))}
            isClearable
            placeholder="All types"
          >
            <option value="">All types</option>
            {ZONE_EVENT_TYPES.map((t) => (
              <option key={t.type} value={t.type}>
                {t.label}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div>
          <label htmlFor="zone-events-zone" className={labelClass}>
            Zone
          </label>
          <ThemedSelect
            id="zone-events-zone"
            aria-label="Zone"
            value={geofenceId}
            onChange={(e) => setGeofenceId(String(e.target.value ?? ''))}
            isClearable
            placeholder="All zones"
          >
            <option value="">All zones</option>
            {geofences.map((g) => (
              <option key={g.GeofenceID} value={String(g.GeofenceID)}>
                {g.Name}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div>
          <label htmlFor="zone-events-vehicle" className={labelClass}>
            Vehicle
          </label>
          <input
            id="zone-events-vehicle"
            type="search"
            value={regInput}
            onChange={(e) => setRegInput(e.target.value)}
            placeholder="Registration"
            className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm bg-surface text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>
      </div>

      {ackError && (
        <div
          role="alert"
          className="mb-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2"
        >
          {ackError}
        </div>
      )}

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setReloadNonce((n) => n + 1)}
            className="font-semibold underline"
          >
            Retry
          </button>
        </div>
      ) : loading && items.length === 0 ? (
        <div
          role="status"
          className="flex items-center gap-2 py-10 justify-center text-sm text-gray-500"
        >
          <span
            className="inline-block w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          />
          Loading zone events...
        </div>
      ) : !available ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
          <FaClock className="mx-auto text-gray-300 mb-3" size={40} aria-hidden="true" />
          <p className="text-gray-600 text-sm font-medium">
            Zone events are not set up on this server yet
          </p>
          <p className="text-gray-500 text-xs mt-1">LexiStar records them once it is installed.</p>
        </div>
      ) : shown.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
          <FaClock className="mx-auto text-gray-300 mb-3" size={40} aria-hidden="true" />
          <p className="text-gray-600 text-sm font-medium">
            {show === 'open'
              ? 'No open alerts for these dates'
              : 'No zone events for these dates and filters'}
          </p>
          <p className="text-gray-500 text-xs mt-1">
            LexiStar records an event when a vehicle enters or leaves a zone, or when a zone rule is
            broken.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {shown.map((event) => {
            const tone = TONES[typeTone(event.type)];
            const open = isPendingAlert(event);
            const detail = eventDetail(event);
            return (
              <li
                key={event.id}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  // An open alert is outlined; one of Medium severity or above is also filled.
                  open
                    ? SEVERITY_CLASS[event.severity]
                      ? 'border-amber-200 bg-amber-50'
                      : 'border-amber-200 bg-surface'
                    : 'border-gray-200 bg-surface'
                }`}
              >
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${tone.dot}`}
                  aria-hidden="true"
                >
                  {tone.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-medium text-gray-900">
                      {event.regNo || 'Site'}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${tone.badge}`}>
                      {typeLabel(event.type)}
                    </span>
                    {event.alert && SEVERITY_CLASS[event.severity] && (
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded font-medium ${SEVERITY_CLASS[event.severity]}`}
                      >
                        {SEVERITY_LABELS[event.severity]}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {event.geofenceName ||
                      (event.geofenceId ? `Zone #${event.geofenceId}` : 'Zone')}
                    {detail && ` — ${detail}`}
                  </p>
                </div>
                <div
                  className="text-xs text-gray-500 flex-shrink-0 text-right"
                  title={formatAppDateTime(event.eventTime)}
                >
                  {formatRelativeTime(event.eventTime)}
                </div>
                {open ? (
                  <button
                    type="button"
                    onClick={() => acknowledge(event)}
                    disabled={acking === event.id}
                    className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-surface border border-gray-300 hover:border-green-500 hover:text-green-700 rounded transition-colors disabled:opacity-50"
                    aria-label={`Acknowledge the alert for ${event.regNo || 'this event'} at ${event.geofenceName || 'the zone'}`}
                  >
                    <FaCheck size={11} aria-hidden="true" />
                    Acknowledge
                  </button>
                ) : event.alert && event.status !== 'open' ? (
                  <span
                    className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-green-600"
                    title="Acknowledged"
                  >
                    <FaCheck size={11} aria-hidden="true" />
                    <span className="sr-only">Acknowledged</span>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {!error && items.length < total && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-surface border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {loadingMore ? 'Loading...' : `Show more (${total - items.length} more)`}
          </button>
        </div>
      )}
    </div>
  );
}
